// HCEN Patient Website — Review Prompt
// Mirrors lib/services/review_service.dart + home_screen pop-up.
// Polls /reviews/pending every 2 minutes for the logged-in patient.
// Shows a bottom-sheet review modal automatically with three options:
//   • Submit Review     → POST /reviews
//   • Maybe later       → snooze this bookingId for 4 hours (localStorage)
//   • Don't ask         → permanently skip this bookingId (localStorage)
// Mounted once in <Shell /> so it works across every authenticated page.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ReviewRatingSection from './ReviewRatingSection';

const POLL_MS        = 2 * 60 * 1000;          // poll cadence (2 min)
const SNOOZE_MS      = 4 * 60 * 60 * 1000;     // "Maybe later" = 4 h
const SKIPPED_KEY    = 'hcen_review_skipped_v1';
const SNOOZED_KEY    = 'hcen_review_snoozed_v1';

const TAGS = [
  'Clean facility', 'Friendly staff', 'Short wait',
  'Doctor was great', 'Affordable', 'Easy check-in',
];
// ── Local persistence helpers ─────────────────────────────────────
const readSet = (key) => {
  try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')); }
  catch { return new Set(); }
};
const writeSet = (key, set) => {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch {}
};
const readMap = (key) => {
  try { return new Map(Object.entries(JSON.parse(localStorage.getItem(key) || '{}'))); }
  catch { return new Map(); }
};
const writeMap = (key, map) => {
  try { localStorage.setItem(key, JSON.stringify(Object.fromEntries(map))); } catch {}
};

const isHidden = (bookingId) => {
  if (readSet(SKIPPED_KEY).has(bookingId)) return true;
  const snoozed = readMap(SNOOZED_KEY);
  const until = Number(snoozed.get(bookingId) || 0);
  if (until && Date.now() < until) return true;
  // Cleanup expired snoozes lazily.
  if (until && Date.now() >= until) {
    snoozed.delete(bookingId);
    writeMap(SNOOZED_KEY, snoozed);
  }
  return false;
};
const snooze = (bookingId) => {
  const m = readMap(SNOOZED_KEY);
  m.set(bookingId, String(Date.now() + SNOOZE_MS));
  writeMap(SNOOZED_KEY, m);
};
const skipForever = (bookingId) => {
  const s = readSet(SKIPPED_KEY); s.add(bookingId); writeSet(SKIPPED_KEY, s);
};

export default function ReviewPrompt() {
  const { user, showToast } = useAuth();
  const [active, setActive] = useState(null);   // booking awaiting review
  const pollRef             = useRef(null);
  const queueRef            = useRef([]);

  const checkPending = useCallback(async () => {
    if (!user?.patientId) return;
    if (active) return;                          // sheet already open
    const r = await api.pendingReviews(user.patientId);
    const list = (r.data?.pending || []).filter(b => !isHidden(b.bookingId));
    queueRef.current = list;
    if (list.length > 0) setActive(list[0]);
  }, [user, active]);

  // Start/stop polling whenever auth changes.
  useEffect(() => {
    if (!user) return;
    checkPending();                              // initial check
    pollRef.current = setInterval(checkPending, POLL_MS);
    const onVisible = () => { if (!document.hidden) checkPending(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, checkPending]);

  if (!active) return null;

  const closeAndAdvance = (action) => {
    const id = active.bookingId;
    if (action === 'snooze') snooze(id);
    if (action === 'skip')   skipForever(id);
    // Advance to next pending review (if any) after a short delay so the
    // closing animation doesn't fight with the next sheet's opening one.
    setActive(null);
    setTimeout(() => {
      const next = queueRef.current.find(b =>
        b.bookingId !== id && !isHidden(b.bookingId)
      );
      if (next) setActive(next);
    }, 250);
  };

  return (
    <ReviewSheet
      key={active.bookingId}
      booking={active}
      patientId={user.patientId}
      onSubmitted={() => {
        showToast('Thanks for your review ⭐', 'success');
        closeAndAdvance('submitted');
      }}
      onLater={() => closeAndAdvance('snooze')}
      onSkip={()  => closeAndAdvance('skip')}
    />
  );
}

// ─── Sheet UI (matches MyReviewsPage modal + adds Don't-ask) ─────
function ReviewSheet({ booking, patientId, onSubmitted, onLater, onSkip }) {
  const { showToast } = useAuth();
  const [hStars, setHStars]   = useState(0);
  const [dStars, setDStars]   = useState(0);
  const [tags, setTags]       = useState([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy]       = useState(false);

  const toggleTag = (t) =>
    setTags(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);

  const submit = async () => {
    if (!hStars) { showToast('Please rate the hospital', 'error'); return; }
    setBusy(true);
    const r = await api.submitReview({
      bookingId:   booking.bookingId,
      patientId,
      stars:       hStars,
      doctorStars: dStars || 0,
      comment:     comment.trim(),
      tags,
    });
    setBusy(false);
    if (r.error) { showToast(r.error, 'error'); return; }
    onSubmitted();
  };

  return (
    <div onClick={onLater} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      zIndex: 9500, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg, #fff)', width: '100%', maxWidth: 540,
        borderRadius: '16px 16px 0 0', maxHeight: '92vh', overflowY: 'auto',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.25)', padding: '20px 18px 24px',
      }}>
        <div style={{ width: 40, height: 4, background: 'var(--border)', borderRadius: 4, margin: '0 auto 14px' }} />
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>How was your visit?</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 18 }}>
          {booking.hospitalName}{booking.doctorName ? ` · ${booking.doctorName}` : ''}
        </div>

        <ReviewRatingSection
          icon="🏥"
          title="Rate the hospital"
          subtitle={booking.hospitalName || 'Hospital'}
          value={hStars}
          onChange={setHStars}
          accent="#3b82f6"
          emptyHint="Tap to rate hospital"
        />
        {booking.doctorName && (
          <ReviewRatingSection
            icon="👨‍⚕️"
            title="Rate the doctor"
            subtitle={booking.doctorName}
            value={dStars}
            onChange={setDStars}
            accent="#10b981"
            emptyHint="Optional · tap to rate doctor"
          />
        )}

        {/* Tags */}
        <div style={{ fontWeight: 700, marginBottom: 8 }}>What stood out?</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {TAGS.map(t => {
            const sel = tags.includes(t);
            return (
              <button key={t} type="button" onClick={() => toggleTag(t)} style={{
                padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: '1px solid', borderColor: sel ? 'var(--red, #d32f2f)' : 'var(--border)',
                background: sel ? 'rgba(211,47,47,0.10)' : 'transparent',
                color: sel ? 'var(--red, #d32f2f)' : 'var(--text-muted)',
              }}>{t}</button>
            );
          })}
        </div>

        {/* Comment */}
        <textarea value={comment} onChange={e => setComment(e.target.value)}
          rows={3} maxLength={500} placeholder="Tell us more (optional)…"
          className="input"
          style={{ width: '100%', padding: 10, fontSize: 14, resize: 'vertical', fontFamily: 'inherit', marginBottom: 6 }} />
        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>
          {comment.length}/500
        </div>

        <button className="btn btn-primary btn-full btn-lg" onClick={submit} disabled={busy || !hStars}
          style={{ marginBottom: 10 }}>
          {busy ? 'Submitting…' : 'Submit Review'}
        </button>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost btn-sm" onClick={onLater} disabled={busy} style={{ flex: 1 }}>
            Maybe later
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onSkip} disabled={busy} style={{ flex: 1 }}>
            Don't ask
          </button>
        </div>
      </div>
    </div>
  );
}

